import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Page,
  Nav,
  NavInner,
  NavBrand,
  NavAnchors,
  NavAnchor,
  Hero,
  HeroGrid,
  HeroVisual,
  H1,
  H2,
  Eyebrow,
  Lead,
  Body,
  Row,
  BtnPrimary,
  BtnPrimaryNative,
  BtnGhost,
  Section,
  SectionMuted,
  SectionInner,
  TwoCol,
  Glass,
  CardGrid,
  Card,
  CardTitle,
  CardText,
  Col3,
  IconCol,
  AccordionRoot,
  AccordionItem,
  AccordionHead,
  AccordionPanel,
  GovernanceTeamsBlock,
  GovernanceTeamHeading,
  GovernanceListLink,
  DocList,
  DocRow,
  Form,
  Input,
  TextArea,
  FormError,
  ContactSuccessBox,
  FooterBar,
  CertPreview,
  GradientText,
} from "./styles";
import B4CarbonAssetsSection from "./B4CarbonAssetsSection";
import B4CompensationsArea from "./B4CompensationsArea";
import { b4ImageForLang } from "./b4Media";
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageFlags from "../../components/LanguageFlags";
import { getTranslation } from "../../locales";
import { suporteService } from "../../services/suporteService";

/** Páginas oficiais citadas no briefing B4 (ajuste paths de projeto quando houver URLs definitivas). */
const B4_PLATFORM_URL = "https://b4.capital/";
const B4_PROJETOS_LISTADOS_URL =
  "https://b4.capital/pt/projetos-especiais-b4/#listados";
const VERT_HOMOLOGADA_B4_URL =
  "https://b4.capital/pt/vertecotech-empresa-homologada-na-b4/";
const WILDER_BLOCKCHAIN_EXPLORER_URL = "https://blockexplorer.wilder.earth/";
const SOCIAL_CARBON_REGISTRY_URL = "https://registry.socialcarbon.org";

function Accordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <AccordionRoot>
      {items.map((item, i) => (
        <AccordionItem key={item.title}>
          <AccordionHead
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span>
              {item.title}
            </span>
            <span aria-hidden>{open === i ? "−" : "+"}</span>
          </AccordionHead>
          {open === i && (
            <AccordionPanel>
              {item.content ? (
                <p>
                  {item.content}
                </p>
              ) : null}
              {item.list && (
                <ul>
                  {item.list.map((l) => (
                    <li key={l}>
                      {l}
                    </li>
                  ))}
                </ul>
              )}
            </AccordionPanel>
          )}
        </AccordionItem>
      ))}
    </AccordionRoot>
  );
}

function GovernanceTeams({ teams }) {
  if (!teams) return null;
  const href = teams.quemSomosUrl;
  return (
    <GovernanceTeamsBlock>
      <GovernanceTeamHeading>
        {teams.technicalHeading}
      </GovernanceTeamHeading>
      <ul>
        {teams.technicalMembers.map((member) => (
          <li key={member.name}>
            <GovernanceListLink
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {member.name}
            </GovernanceListLink>
          </li>
        ))}
      </ul>
      <ul>
        <li>
          <GovernanceListLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {teams.projectMgmtLinkLabel}
          </GovernanceListLink>
        </li>
      </ul>
    </GovernanceTeamsBlock>
  );
}

const B4VertEcotechPage = () => {
  const { language } = useLanguage();
  const t = getTranslation(language).b4VertPage;
  const [contactForm, setContactForm] = useState({
    nome: "",
    email: "",
    mensagem: "",
  });
  const [contactErrors, setContactErrors] = useState({});
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    document.documentElement.lang = language === "en" ? "en" : "pt-BR";
    const meta = document.querySelector('meta[name="description"]');
    document.title = t.meta.title;
    meta?.setAttribute("content", t.meta.description);
  }, [language, t.meta.title, t.meta.description]);

  const docItems = [
    { title: t.docs.linkTitles.creditVerification, href: SOCIAL_CARBON_REGISTRY_URL },
    { title: t.docs.linkTitles.platformAccess, href: B4_PLATFORM_URL },
    { title: t.docs.linkTitles.vertHomologated, href: VERT_HOMOLOGADA_B4_URL },
    { title: t.docs.linkTitles.projectsListed, href: B4_PROJETOS_LISTADOS_URL },
    { title: t.docs.linkTitles.blockchain, href: WILDER_BLOCKCHAIN_EXPLORER_URL },
  ];

  const validateContact = () => {
    const e = {};
    const err = t.contact.errors;
    if (!contactForm.nome.trim()) e.nome = err.nome;
    if (!contactForm.email.trim()) e.email = err.email;
    if (!contactForm.mensagem.trim()) e.mensagem = err.mensagem;
    else if (contactForm.mensagem.length > 10000) e.mensagem = err.mensagemLong;
    return e;
  };

  const handleContact = async (e) => {
    e.preventDefault();
    const validation = validateContact();
    if (Object.keys(validation).length > 0) {
      setContactErrors(validation);
      return;
    }

    setContactErrors({});
    setContactLoading(true);

    try {
      const result = await suporteService.enviarMensagem(
        contactForm.nome,
        contactForm.email,
        contactForm.mensagem
      );

      if (result.ok) {
        setContactSuccess(true);
        setContactForm({ nome: "", email: "", mensagem: "" });
        return;
      }

      if (result.kind === "validation") {
        const d = result.data;
        setContactErrors({
          nome: d.nome?.[0],
          email: d.email?.[0],
          mensagem: d.mensagem?.[0],
        });
        return;
      }

      if (result.kind === "rate_limit") {
        setContactErrors({ geral: t.contact.errors.rateLimit });
        return;
      }

      setContactErrors({ geral: t.contact.errors.general });
    } catch {
      setContactErrors({ geral: t.contact.errors.connection });
    } finally {
      setContactLoading(false);
    }
  };

  const resetContactForm = () => {
    setContactSuccess(false);
    setContactErrors({});
    setContactForm({ nome: "", email: "", mensagem: "" });
  };

  return (
    <Page id="topo">
      <Nav>
        <NavInner>
          <NavBrand>
            <Link
              to="/"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Vert Ecotech · 
              <GradientText>
                B4, Bolsa de Ação Climática
              </GradientText>
            </Link>
          </NavBrand>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "0.75rem 1rem",
              flex: "1 1 auto",
            }}
          >
            <NavAnchors>
              <NavAnchor href="#hero">{t.nav.home}</NavAnchor>
              <NavAnchor href="#sobre">{t.nav.about}</NavAnchor>
              <NavAnchor href="#parceria">{t.nav.partnership}</NavAnchor>
              <NavAnchor href="#certificado">{t.nav.certificate}</NavAnchor>
              <NavAnchor href="#governanca">{t.nav.compliance}</NavAnchor>
              <NavAnchor href="#projetos">{t.nav.projects}</NavAnchor>
              <NavAnchor href="#compartilhamento-beneficios">{t.nav.benefitSharing}</NavAnchor>
              <NavAnchor href="#aposentadorias">{t.nav.retirements}</NavAnchor>
              <NavAnchor href="#metodologia">{t.nav.methodology}</NavAnchor>
              <NavAnchor href="#documentacao">{t.nav.docs}</NavAnchor>
              <NavAnchor href="#contato">{t.nav.contact}</NavAnchor>
            </NavAnchors>
            <LanguageFlags variant="dark" />
          </div>
        </NavInner>
      </Nav>

      <Hero id="hero">
        <HeroGrid>
          <div>
            <Eyebrow>{t.hero.eyebrow}</Eyebrow>
            <H1>
              {t.hero.titleBefore}{" "}
              <GradientText>
                B4, Bolsa de Ação Climática
              </GradientText>
            </H1>
            <Lead>
              {t.hero.lead}
            </Lead>
            <Row>
              <BtnPrimary href="#projetos">{t.hero.ctaProjects}</BtnPrimary>
              <BtnGhost href={B4_PLATFORM_URL} target="_blank" rel="noopener noreferrer">
                {t.hero.ctaPlatform}
              </BtnGhost>
            </Row>
          </div>
          <HeroVisual
            style={{
              position: "relative",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              padding: "1.25rem 0.75rem",
              minHeight: "clamp(260px, 36vw, 340px)",
            }}
          >
            <img
              key={`selo-${language}`}
              src={b4ImageForLang(language, "selo")}
              alt={t.hero.altSeal}
              style={{
                width: "auto",
                height: "clamp(220px, 38vw, 320px)",
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: 12,
              }}
            />
          </HeroVisual>
        </HeroGrid>
      </Hero>

      <Section id="sobre">
        <TwoCol>
          <div>
            <Eyebrow>{t.about.eyebrow}</Eyebrow>
            <H2>{t.about.h2}</H2>
            <Body>
              {t.about.body1}
            </Body>
            <Body style={{ marginTop: "1rem" }}>
              {t.about.body2}
            </Body>
            <Row style={{ marginTop: "1.5rem" }}>
              <BtnGhost href={B4_PLATFORM_URL} target="_blank" rel="noopener noreferrer">
                {t.about.btnOfficial}
              </BtnGhost>
            </Row>
          </div>
            <Glass>
              <CardTitle>{t.about.trustTitle}</CardTitle>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "1rem",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "0.65rem",
                }}
              >
                <img
                  key={`miniCert-${language}`}
                  src={b4ImageForLang(language, "miniCert")}
                  alt={t.about.altMini}
                  style={{
                    width: "100%",
                    maxWidth: 260,
                    height: "auto",
                    borderRadius: 12,
                    display: "block",
                  }}
                />
              </div>
            </Glass>
        </TwoCol>
      </Section>

      <SectionMuted id="parceria">
        <SectionInner>
          <Eyebrow>{t.partnership.eyebrow}</Eyebrow>
          <H2>{t.partnership.h2}</H2>
          <Lead style={{ maxWidth: "44rem" }}>
            {t.partnership.lead1}
          </Lead>
          <Lead style={{ maxWidth: "44rem", marginTop: "0.5rem", fontWeight: 600, color: "#e8f4fc" }}>
            {t.partnership.allowsLead}
          </Lead>
          <CardGrid>
            {t.partnership.cards.map((card) => (
              <Card key={card.title}>
                <CardTitle>{card.title}</CardTitle>
                <CardText>{card.text}</CardText>
              </Card>
            ))}
          </CardGrid>
        </SectionInner>
      </SectionMuted>

      <Section id="certificado">
        <TwoCol style={{ alignItems: "start" }}>
          <div>
            <Eyebrow>{t.certificate.eyebrow}</Eyebrow>
            <H2>{t.certificate.h2}</H2>
            <Body>
            {t.certificate.body}
            </Body>
            <Row>
              <BtnPrimary as={Link} to="/certificado">
                {t.certificate.viewCert}
              </BtnPrimary>
              <BtnGhost href={B4_PLATFORM_URL} target="_blank" rel="noopener noreferrer">
                {t.certificate.validate}
              </BtnGhost>
              <BtnGhost href={VERT_HOMOLOGADA_B4_URL} target="_blank" rel="noopener noreferrer" style={{ borderColor: "#2a6b5e" }}>
                {t.certificate.vertHomologated}
              </BtnGhost>
            </Row>
          </div>
            <CertPreview>
              <img
                key={`cert-${language}`}
                src={b4ImageForLang(language, "cert")}
                alt={t.certificate.altCert}
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: 12,
                  display: "block",
                }}
              />
            </CertPreview>
        </TwoCol>
      </Section>

      <Section id="governanca">
        <Eyebrow>{t.governance.eyebrow}</Eyebrow>
        <H2>{t.governance.h2}</H2>
        <Lead>
          {t.governance.lead}
        </Lead>
        <Accordion items={t.governance.items} />
        <GovernanceTeams teams={t.governance.teams} />
      </Section>

      <B4CarbonAssetsSection />

      <B4CompensationsArea />

      <Section id="metodologia">
        <Eyebrow>{t.methodology.eyebrow}</Eyebrow>
        <H2>{t.methodology.h2}</H2>
        <Col3>
          <IconCol>
            <h3>{t.methodology.col1h}</h3>
            <ul>
              {t.methodology.col1items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </IconCol>
          <IconCol>
            <h3>{t.methodology.col2h}</h3>
            <ul>
              {t.methodology.col2items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </IconCol>
          <IconCol>
            <h3>{t.methodology.col3h}</h3>
            <ul>
              {t.methodology.col3items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </IconCol>
        </Col3>
      </Section>

      <Section id="documentacao">
        <Eyebrow>{t.docs.eyebrow}</Eyebrow>
        <H2>{t.docs.h2}</H2>
        <DocList>
          {docItems.map((d) => {
            const needsExternal = Boolean(d.drivePlaceholder) || d.href === "#" || d.href === "";
            return (
              <DocRow
                key={d.title}
                style={
                  needsExternal
                    ? {
                        outline: "2px dashed #e53935",
                        outlineOffset: "2px",
                        background: "rgba(229, 57, 53, 0.08)",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: "0.5rem",
                      }
                    : undefined
                }
              >
                {needsExternal ? (
                  <span
                    style={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "#ffcdd2",
                      background: "rgba(198, 40, 40, 0.95)",
                      padding: "0.3rem 0.45rem",
                      borderRadius: "4px",
                      alignSelf: "flex-start",
                    }}
                  >
                    
                      {d.placeholderRef
                        ? `${d.placeholderRef} ${t.docs.placeholderDriveSuffix}`
                        : t.docs.placeholderLink}
                    
                  </span>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                    width: "100%",
                  }}
                >
                  <span style={{ color: "#f5fbff", fontWeight: 600 }}>
                    {d.title}
                  </span>
                  {d.href ? (
                    <BtnGhost
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem" }}
                    >
                      {t.docs.accessBtn}
                    </BtnGhost>
                  ) : null}
                </div>
              </DocRow>
            );
          })}
        </DocList>
      </Section>

      <SectionMuted id="contato">
        <SectionInner>
          <Eyebrow>{t.contact.eyebrow}</Eyebrow>
          <H2>{t.contact.h2}</H2>
          <Lead>{t.contact.lead}</Lead>
          {contactSuccess ? (
            <ContactSuccessBox>
              <h3>{t.contact.successTitle}</h3>
              <p>{t.contact.successText}</p>
              <Row style={{ marginTop: "1.25rem" }}>
                <BtnPrimaryNative type="button" onClick={resetContactForm}>
                  {t.contact.sendAnother}
                </BtnPrimaryNative>
              </Row>
            </ContactSuccessBox>
          ) : (
            <Form onSubmit={handleContact} noValidate>
              <Input
                name="name"
                type="text"
                placeholder={t.contact.namePh}
                value={contactForm.nome}
                onChange={(ev) =>
                  setContactForm({ ...contactForm, nome: ev.target.value })
                }
                disabled={contactLoading}
              />
              {contactErrors.nome ? (
                <FormError>{contactErrors.nome}</FormError>
              ) : null}
              <Input
                name="email"
                type="email"
                placeholder={t.contact.emailPh}
                value={contactForm.email}
                onChange={(ev) =>
                  setContactForm({ ...contactForm, email: ev.target.value })
                }
                disabled={contactLoading}
              />
              {contactErrors.email ? (
                <FormError>{contactErrors.email}</FormError>
              ) : null}
              <TextArea
                name="message"
                placeholder={t.contact.messagePh}
                value={contactForm.mensagem}
                onChange={(ev) =>
                  setContactForm({ ...contactForm, mensagem: ev.target.value })
                }
                maxLength={10000}
                disabled={contactLoading}
              />
              {contactErrors.mensagem ? (
                <FormError>{contactErrors.mensagem}</FormError>
              ) : null}
              {contactErrors.geral ? (
                <FormError>{contactErrors.geral}</FormError>
              ) : null}
              <Row>
                <BtnPrimaryNative type="submit" disabled={contactLoading}>
                  {contactLoading ? t.contact.sending : t.contact.submit}
                </BtnPrimaryNative>
              </Row>
            </Form>
          )}
        </SectionInner>
      </SectionMuted>

      <FooterBar>
        
          {`© ${new Date().getFullYear()} Vert Ecotech · B4, Bolsa de Ação Climática — ${t.footer.climateExchange}`}
        
        {" · "}
        <Link to="#topo" style={{ color: "#10b7a6" }}>
          {t.footer.top}
        </Link>
        {" · "}
        <Link to="/old_landing_page" style={{ color: "#6b8794", fontSize: "0.75rem" }}>
          {t.footer.legacyRef}
        </Link>
      </FooterBar>
    </Page>
  );
};

export default B4VertEcotechPage;
